import {
  buyerPhonePrefixSelectOptions,
  buyerCountrySelectOptions,
  useRouter,
  binarySelectOptions,
} from '@application/utils';
import type { CountryCode } from '@gql/graphql';
import {
  Button,
  InputCheckboxField,
  InputField,
  InputLabel,
  InputRadioField,
  InputSelectField,
  InputWrapper,
  Section,
} from '@presentation/components';
import { ConditionalWrapper, useTranslation } from '@presentation/utils';
import { Form, useFormikContext } from 'formik';
import type { FC } from 'react';
import { FaFacebookF } from 'react-icons/fa';
import googleIcon from './assets/google-icon.svg';
import * as Styled from './styled';
import type { JoinBuyerFormProps, JoinBuyerFormValues } from './types';

export * from './types';

export const JoinBuyerForm: FC<JoinBuyerFormProps> = (props) => {
  const {
    loginLink,
    onFacebookClick,
    onGoogleClick,
    onSubmitClick,
    loading = false,
    showPostSignup,
    showHasADogField = true,
    requireMarketingEmailConsent,
  } = props;
  const { translate } = useTranslation();
  const { routeLocationDescriptor } = useRouter();
  const countryOptions = buyerCountrySelectOptions(translate);
  const phonePrefixes = buyerPhonePrefixSelectOptions(translate);

  const yesNoOptions = binarySelectOptions(translate);

  const { errors } = useFormikContext<JoinBuyerFormValues>();

  if (showPostSignup) {
    return (
      <Section>
        <Form>
          <InputWrapper>
            <InputLabel required>{translate('common.inputs.phone.label')}</InputLabel>

            <InputWrapper inline width="39%" fullWidthBreakpoint="min" noMargin>
              <InputSelectField name="phonePrefixCountry" options={phonePrefixes} />
            </InputWrapper>

            <InputWrapper inline width="61%" fullWidthBreakpoint="min" last noMargin>
              <InputField name="phoneNumber" type="tel" />
            </InputWrapper>
          </InputWrapper>

          <InputWrapper>
            <InputLabel required>{translate('common.inputs.country.label')}</InputLabel>
            <InputSelectField<string> name="country" options={countryOptions} />
          </InputWrapper>

          {showHasADogField ? (
            <InputWrapper>
              <InputLabel required>{translate('common.inputs.hasADog')}</InputLabel>
              <InputRadioField<boolean>
                name="hasADog"
                variant="blue"
                grow
                options={yesNoOptions}
                hasError={!!errors.hasADog}
              />
            </InputWrapper>
          ) : null}

          <InputWrapper>
            <Button color="blue" onClick={onSubmitClick} loading={loading} type="submit">
              {translate('common.buttons.register')}
            </Button>
          </InputWrapper>
        </Form>
      </Section>
    );
  }

  return (
    <>
      <InputWrapper>
        <Button color="white" outlined onClick={onGoogleClick}>
          <Styled.SsoButtonIconWrapper>
            <img src={googleIcon} alt="" />
          </Styled.SsoButtonIconWrapper>
          {translate('common.buttons.registerWithGoogle')}
        </Button>
      </InputWrapper>
      <InputWrapper>
        <Styled.SsoButtonFacebook color="blue" onClick={onFacebookClick}>
          <Styled.SsoButtonIconWrapper>
            <FaFacebookF />
          </Styled.SsoButtonIconWrapper>
          {translate('common.buttons.registerWithFacebook')}
        </Styled.SsoButtonFacebook>
      </InputWrapper>

      <Styled.Separator />

      <Section>
        <Form>
          <InputWrapper>
            <InputLabel required>{translate('common.inputs.firstName.label')}</InputLabel>
            <InputField name="firstName" />
          </InputWrapper>

          <InputWrapper>
            <InputLabel required>{translate('common.inputs.lastName.label')}</InputLabel>
            <InputField name="lastName" />
          </InputWrapper>

          <InputWrapper>
            <InputLabel required>{translate('common.inputs.email.label')}</InputLabel>
            <InputField name="email" type="email" />
          </InputWrapper>

          <InputWrapper>
            <InputLabel required>{translate('common.inputs.phone.label')}</InputLabel>

            <InputWrapper inline width="39%" fullWidthBreakpoint="min" noMargin>
              <InputSelectField name="phonePrefixCountry" options={phonePrefixes} />
            </InputWrapper>

            <InputWrapper inline width="61%" fullWidthBreakpoint="min" last noMargin>
              <InputField name="phoneNumber" type="tel" />
            </InputWrapper>
          </InputWrapper>

          <InputWrapper>
            <InputLabel required>{translate('common.inputs.country.label')}</InputLabel>
            <InputSelectField<CountryCode> name="country" options={countryOptions} />
          </InputWrapper>

          <InputWrapper>
            <InputLabel required>{translate('common.inputs.password.label')}</InputLabel>
            <InputField name="password" type="password" />
          </InputWrapper>

          <InputWrapper>
            <InputLabel required>{translate('common.inputs.passwordRepeat.label')}</InputLabel>
            <InputField name="confirmPassword" type="password" />
          </InputWrapper>
          {showHasADogField ? (
            <InputWrapper>
              <InputLabel required>{translate('common.inputs.hasADog')}</InputLabel>
              <InputRadioField<boolean>
                name="hasADog"
                variant="blue"
                grow
                options={yesNoOptions}
                hasError={!!errors.hasADog}
              />
            </InputWrapper>
          ) : null}

          <ConditionalWrapper
            condition={requireMarketingEmailConsent}
            wrapper={(wrapperChildren) => (
              <Styled.CheckboxWrapper>{wrapperChildren}</Styled.CheckboxWrapper>
            )}
          >
            <InputWrapper>
              <InputCheckboxField name="acceptedTerms">
                <Styled.LinkSpan
                  dangerouslySetInnerHTML={{
                    __html: translate('signup.termsPolicy').replace(
                      '$href',
                      routeLocationDescriptor('terms').pathname ?? '',
                    ),
                  }}
                />
              </InputCheckboxField>
            </InputWrapper>

            {requireMarketingEmailConsent ? (
              <InputWrapper>
                <InputCheckboxField name="marketingEmailConsent">
                  <Styled.LinkSpan
                    dangerouslySetInnerHTML={{
                      __html: translate('common.inputs.termsPolicyEmailMarketing.label').replace(
                        '$link',
                        routeLocationDescriptor('privacy').pathname ?? '',
                      ),
                    }}
                  />
                </InputCheckboxField>
              </InputWrapper>
            ) : null}
          </ConditionalWrapper>

          <InputWrapper>
            <Button color="blue" onClick={onSubmitClick} loading={loading} type="submit">
              {translate('common.buttons.register')}
            </Button>
          </InputWrapper>

          {!!loginLink && (
            <InputWrapper>
              <Styled.LoginWrapper>
                <span>{translate('signup.alreadyHasAccount')}</span>
                <Styled.LoginLink to={loginLink}>
                  {translate('common.buttons.login')}
                </Styled.LoginLink>
              </Styled.LoginWrapper>
            </InputWrapper>
          )}
        </Form>
      </Section>
    </>
  );
};
